import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import {
  clearEnterpriseEmulationStash,
  readEnterpriseEmulationStash,
  writeEnterpriseEmulationStash,
  type EnterpriseEmulationStash,
} from '@/lib/enterprise-emulation';

/**
 * Drives the ENTERPRISE-admin emulation flow — parallel to
 * useImpersonation but a fully separate trust tier (spec §6.3–6.5):
 *   - `start()`: authorize (server) + audit insert + session swap into
 *     the target member's JWT (full read-write, no notification)
 *   - `end()`: close the audit row + restore the admin's session
 *   - `cancel()`: close an orphan audit row (verifyOtp never landed)
 *   - `state`: current emulation context (admin-side, or null)
 *
 * Source of truth lives in two places that must agree:
 *   1. sessionStorage stash (`__buildit_enterprise_emulation_v1`)
 *   2. current_enterprise_emulation_context() RPC (server-side)
 * If the two disagree, we clear OUR OWN stash and report inactive
 * (self-clearing guard — spec §6.4.1; each loop touches only its key).
 */

interface StartArgs {
  partner_id: string;
  target_user_id: string;
  target_email: string;
  reason: string;
  password: string;
}

/**
 * Error thrown by `start()` when the server refuses. Exposes the
 * server-side stable `code` (e.g. 'open_session_exists',
 * 'caller_not_admin_of_partner', 'password_verification_failed') so
 * callers can branch the UX — notably the 409 recover-and-retry path.
 */
export class EnterpriseEmulationStartError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'EnterpriseEmulationStartError';
    this.code = code;
  }
}

interface ServerContext {
  audit_id: string;
  admin_user_id: string;
  partner_id: string;
  partner_name: string;
  started_at: string;
  read_only: boolean;
  reason: string;
  mode: 'enterprise_admin';
}

export type EnterpriseEmulationState =
  | { active: false }
  | {
      active: true;
      audit_id: string;
      admin_email: string;
      target_email: string;
      target_user_id: string;
      partner_id: string;
      partner_name: string;
      started_at: string;
      read_only: boolean;
      reason: string;
    };

const FUNCTIONS_BASE =
  (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/?$/, '') + '/functions/v1';

async function callServerContext(): Promise<ServerContext | null> {
  const { data, error } = await supabase.rpc('current_enterprise_emulation_context');
  if (error) {
    // Mirror useImpersonation.callServerContext: errors here are raised at
    // the PostgREST gateway BEFORE the function runs — auth-layer failures
    // (PGRST3xx, expired/in-transition JWT) during the session swap. The
    // reconcile loop treats a null context as "not emulating, clear stash",
    // which is correct, so these are benign and must not page. Only capture
    // genuinely unexpected errors (wrapped in a real Error so the message
    // is actionable rather than "Object captured as exception").
    const isAuthTransient =
      error.code?.startsWith('PGRST3') || /jwt/i.test(error.message ?? '');
    if (!isAuthTransient) {
      captureError(
        new Error(
          `current_enterprise_emulation_context failed: ${error.message} (${error.code})`,
        ),
        { feature: 'enterprise-emulation-context', code: error.code, details: error.details },
      );
    }
    return null;
  }
  return (data as ServerContext | null) ?? null;
}

export function useEnterpriseEmulation() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<EnterpriseEmulationState>({ active: false });
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  // On mount + on auth changes, reconcile stash + server context. This is
  // the ADMIN-side view (the admin holds the stash). The member-side banner
  // does NOT use this hook's stash gate (it has no stash) — see the banner.
  useEffect(() => {
    let cancelled = false;

    async function reconcile() {
      const stash = readEnterpriseEmulationStash();
      if (!stash) {
        if (!cancelled) setState({ active: false });
        return;
      }
      const serverCtx = await callServerContext();
      if (cancelled) return;
      if (!serverCtx || serverCtx.audit_id !== stash.audit_id) {
        // Stash is stale or lying (self-clearing guard — spec §6.4.1).
        clearEnterpriseEmulationStash();
        setState({ active: false });
        return;
      }
      setState({
        active: true,
        audit_id: stash.audit_id,
        admin_email: stash.admin_email,
        target_email: stash.target_email,
        target_user_id: stash.target_user_id,
        partner_id: serverCtx.partner_id,
        partner_name: serverCtx.partner_name,
        started_at: stash.started_at,
        read_only: serverCtx.read_only,
        reason: serverCtx.reason,
      });
    }

    reconcile();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        setTimeout(() => {
          if (!cancelled) reconcile();
        }, 0);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const start = useCallback(
    async (args: StartArgs) => {
      setIsStarting(true);
      try {
        const { data: currentSession } = await supabase.auth.getSession();
        const adminSession = currentSession?.session;
        if (!adminSession?.user?.email || !adminSession.refresh_token) {
          throw new Error('No active admin session to stash.');
        }

        const startRes = await fetch(`${FUNCTIONS_BASE}/start-enterprise-emulation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminSession.access_token}`,
          },
          body: JSON.stringify({
            partner_id: args.partner_id,
            target_user_id: args.target_user_id,
            reason: args.reason,
            password: args.password,
          }),
        });
        if (!startRes.ok) {
          const body = await startRes.json().catch(() => ({}));
          throw new EnterpriseEmulationStartError(
            body?.detail ?? body?.error ?? `start-enterprise-emulation failed (${startRes.status})`,
            body?.error ?? body?.code,
          );
        }
        const payload = (await startRes.json()) as {
          audit_id: string;
          token_hash: string;
          email: string;
          read_only: boolean;
          partner_id: string;
          partner_name: string;
        };

        const stash: EnterpriseEmulationStash = {
          audit_id: payload.audit_id,
          admin_access_token: adminSession.access_token,
          admin_refresh_token: adminSession.refresh_token,
          admin_user_id: adminSession.user.id,
          admin_email: adminSession.user.email,
          target_user_id: args.target_user_id,
          target_email: args.target_email,
          partner_id: payload.partner_id ?? args.partner_id,
          partner_name: payload.partner_name ?? '',
          started_at: new Date().toISOString(),
          read_only: payload.read_only ?? false,
          reason: args.reason,
          mode: 'enterprise_admin',
        };
        writeEnterpriseEmulationStash(stash);

        // Swap the browser session into the target member's. verifyOtp with
        // the magiclink token_hash consumes the one-time token and replaces
        // the active session. The token_hash variant accepts ONLY
        // { token_hash, type } — passing `email` makes GoTrue reject.
        const { error: otpErr } = await supabase.auth.verifyOtp({
          type: 'magiclink',
          token_hash: payload.token_hash,
        });
        if (otpErr) {
          clearEnterpriseEmulationStash();
          // Best-effort: close the audit row we just opened so the admin
          // isn't wedged by the one-open-per-admin index on retry. The
          // explicit "Recover stuck session" button in the modal is the
          // durable recovery (same cancel endpoint).
          try {
            await fetch(`${FUNCTIONS_BASE}/cancel-enterprise-emulation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${adminSession.access_token}`,
              },
              body: '{}',
            });
          } catch (cleanupErr) {
            captureError(cleanupErr as Error, {
              feature: 'enterprise-emulation-orphan-cleanup',
            });
          }
          throw otpErr;
        }

        // Drop the admin's cached query data — react-query refetches under
        // the target member's RLS.
        queryClient.clear();
      } finally {
        setIsStarting(false);
      }
    },
    [queryClient],
  );

  /**
   * Closes any open audit row owned by the current admin without a session
   * swap. Recovers a "started but never session-swapped" orphan when
   * start-enterprise-emulation returns 409 open_session_exists.
   */
  const cancel = useCallback(async () => {
    const { data: currentSession } = await supabase.auth.getSession();
    const adminAccessToken = currentSession?.session?.access_token;
    if (!adminAccessToken) {
      throw new Error('No active admin session.');
    }
    const res = await fetch(`${FUNCTIONS_BASE}/cancel-enterprise-emulation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminAccessToken}`,
      },
      body: '{}',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        body?.detail ?? body?.error ?? `cancel-enterprise-emulation failed (${res.status})`,
      );
    }
  }, []);

  const end = useCallback(async () => {
    setIsEnding(true);
    try {
      const stash = readEnterpriseEmulationStash();
      if (!stash) {
        return;
      }
      const { data: currentSession } = await supabase.auth.getSession();
      const targetAccessToken = currentSession?.session?.access_token;

      // Best-effort close with the target's JWT. If the target token is
      // already invalid, the stale sweep cron closes the row.
      if (targetAccessToken) {
        await fetch(`${FUNCTIONS_BASE}/end-enterprise-emulation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${targetAccessToken}`,
          },
          body: JSON.stringify({ audit_id: stash.audit_id, end_reason: 'admin_exit' }),
        }).catch((err) => {
          captureError(err as Error, { feature: 'enterprise-emulation-end' });
        });
      }

      // Restore the admin's session (PR #525 hardening — spec §6.5).
      const { error: setErr } = await supabase.auth.setSession({
        access_token: stash.admin_access_token,
        refresh_token: stash.admin_refresh_token,
      });
      clearEnterpriseEmulationStash();
      queryClient.clear();
      if (setErr) {
        // setSession failed: the target session is still active in the SDK.
        // Force sign-out before re-raising so the caller can redirect to
        // /login without AuthContext bouncing the still-authenticated
        // (target) session back into the app as the wrong user.
        await supabase.auth.signOut().catch((soErr) => {
          captureError(soErr as Error, {
            feature: 'enterprise-emulation-end-forced-signout',
          });
        });
        throw setErr;
      }
    } finally {
      setIsEnding(false);
    }
  }, [queryClient]);

  return {
    state,
    start,
    end,
    cancel,
    isStarting,
    isEnding,
  };
}

/**
 * Read-only state hook for consumers that only need whether THIS (admin)
 * session is driving an enterprise emulation, derived from the stash.
 * NOTE: the member-concurrent-login banner must NOT depend on this (the
 * member has no stash) — it drives off the server context RPC alone.
 */
export function useEnterpriseEmulationState(): EnterpriseEmulationState {
  const fromStash = (): EnterpriseEmulationState => {
    const stash = readEnterpriseEmulationStash();
    if (!stash) return { active: false };
    return {
      active: true,
      audit_id: stash.audit_id,
      admin_email: stash.admin_email,
      target_email: stash.target_email,
      target_user_id: stash.target_user_id,
      partner_id: stash.partner_id,
      partner_name: stash.partner_name,
      started_at: stash.started_at,
      read_only: stash.read_only,
      reason: stash.reason,
    };
  };

  const [state, setState] = useState<EnterpriseEmulationState>(fromStash);

  useEffect(() => {
    function recompute() {
      setState(fromStash());
    }
    const handler = () => recompute();
    window.addEventListener('storage', handler);
    // storage events don't fire in the same tab — re-check on auth changes.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      setTimeout(recompute, 0);
    });
    return () => {
      window.removeEventListener('storage', handler);
      subscription.unsubscribe();
    };
  }, []);

  return state;
}
